export const colors = {
  primary: {
    100: "#ebf4f0",
    200: "#cdddd5",
    300: "#b0c9bd",
    400: "#93b6a5",
    500: "#76a28d",
    600: "#3d7b5c",
    700: "#206744",
    800: "#03542c",
    900: "#024524",
    1000: "#01341b",
  },
  secondary: {
    100: "#f7f4ff",
    200: "#ede3ff",
    300: "#e3d2ff",
    400: "#d9c1ff",
    500: "#cfadff",
    600: "#b699e6",
    700: "#8A6EC2",
    800: "#5E429E",
    900: "#4A3381",
    1000: "#362464",
  },
  teal: {
    500: "#208074",
    600: "#1a6b62",
    700: "#155a53",
  },
  grey: {
    100: "#f2f2f2",
    200: "#e5e5e5",
    300: "#cccccc",
    400: "#b2b2b2",
    500: "#999999",
    600: "#7f7f7f",
    700: "#666666",
    800: "#4c4c4c",
    900: "#333333",
    1000: "#191919",
  },
  tertiary: {
    100: "#fdfbf8",
    200: "#f5f2ec",
    300: "#ebe9e4",
    400: "#d4d1cd",
    500: "#b7b3ab",
    600: "#938c81",
    700: "#706658",
    800: "#4c402e",
    900: "#281a04",
  },
  orange: {
    100: "#fff4e5",
    900: "#b54708",
  },
  yellow: {
    100: "#fffdeb",
  },
  green: {
    100: "#edf7ed",
    900: "#2e7d32",
  },
  neon: {
    100: "#e7fcf2",
    400: "#93ffd5",
    600: "#56eaa1",
    900: "#0DCB6D",
  },
  white: "#ffffff",
  black: "#000000",
} as const;

export const gradients = {
  buttonFilled: `linear-gradient(180deg, ${colors.primary[800]} 0%, ${colors.teal[500]} 100%)`,
  buttonFilledHover: `linear-gradient(180deg, ${colors.primary[900]} 0%, ${colors.teal[600]} 100%)`,
  buttonFilledActive: `linear-gradient(180deg, ${colors.primary[1000]} 0%, ${colors.teal[700]} 100%)`,
  selection: `linear-gradient(90deg, ${colors.primary[800]}, ${colors.neon[400]}, ${colors.primary[800]})`,
} as const;

export const fonts = {
  inter: "Inter, sans-serif",
  interDisplay: "InterDisplay, sans-serif",
  tiempos: "TiemposText, serif",
  tiemposHeadline: "TiemposHeadline, serif",
} as const;

export const radii = {
  pill: "999px",
  xl: "24px",
  lg: "22px",
  md: "18px",
  base: "16px",
  sm: "10px",
  xs: "8px",
  xxs: "6px",
} as const;

export const spacing = {
  0: "0px",
  0.5: "2px",
  1: "4px",
  1.5: "6px",
  2: "8px",
  2.5: "10px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "28px",
  8: "32px",
  9: "48px",
  10: "64px",
} as const;

export const shadows = {
  card: "1px 1px 16px 0 rgba(195, 177, 139, 0.20)",
  glass: "0px 10px 30px 0px rgba(0,0,0,0.06)",
  navBar: "1px 1px 4.5px rgba(195,177,139,0.2)",
  partnerLogo: "0px 0px 4px rgba(0,0,0,0.18)",
} as const;

export const textVariants = {
  // InterDisplay
  "heading-xl": {fontFamily: fonts.interDisplay, fontWeight: 300, fontSize: "42px", lineHeight: "48px"},
  "heading-lg": {fontFamily: fonts.interDisplay, fontWeight: 300, fontSize: "28px", lineHeight: "32px"},
  heading: {fontFamily: fonts.interDisplay, fontWeight: 300, fontSize: "24px", lineHeight: "32px"},
  "display-lg": {fontFamily: fonts.interDisplay, fontWeight: 400, fontSize: "28px", lineHeight: "32px"},
  "display-semibold": {fontFamily: fonts.interDisplay, fontWeight: 600, fontSize: "18px", lineHeight: "30px"},
  "display-medium": {fontFamily: fonts.interDisplay, fontWeight: 500, fontSize: "16px", lineHeight: "24px"},
  "display-body": {fontFamily: fonts.interDisplay, fontWeight: 400, fontSize: "16px", lineHeight: "24px"},
  greeting: {fontFamily: fonts.interDisplay, fontWeight: 500, fontSize: "15px", lineHeight: "17.5px"},
  "quote-italic": {
    fontFamily: fonts.interDisplay,
    fontWeight: 400,
    fontSize: "20px",
    lineHeight: "30px",
    fontStyle: "italic",
  },

  // Inter
  "title-xl": {fontFamily: fonts.inter, fontWeight: 600, fontSize: "22px"},
  title: {fontFamily: fonts.inter, fontWeight: 600, fontSize: "18px"},
  "label-lg": {fontFamily: fonts.inter, fontWeight: 600, fontSize: "16px", lineHeight: "24px"},
  "body-lg": {fontFamily: fonts.inter, fontWeight: 400, fontSize: "16px", lineHeight: "24px"},
  "body-semibold": {fontFamily: fonts.inter, fontWeight: 600, fontSize: "14px", lineHeight: "22px"},
  "body-medium": {fontFamily: fonts.inter, fontWeight: 500, fontSize: "14px", lineHeight: "22px"},
  body: {fontFamily: fonts.inter, fontWeight: 400, fontSize: "14px", lineHeight: "22px"},
  "caption-medium": {fontFamily: fonts.inter, fontWeight: 500, fontSize: "12px", lineHeight: "18px"},
  "caption-bold": {fontFamily: fonts.inter, fontWeight: 600, fontSize: "12px", lineHeight: "18px"},
  caption: {fontFamily: fonts.inter, fontWeight: 400, fontSize: "12px", lineHeight: "18px"},

  // Tiempos
  "emphasis-light-xl": {
    fontFamily: fonts.tiemposHeadline,
    fontWeight: 300,
    fontSize: "42px",
    fontStyle: "italic",
    lineHeight: "48px",
  },
  "emphasis-light": {
    fontFamily: fonts.tiemposHeadline,
    fontWeight: 300,
    fontSize: "28px",
    fontStyle: "italic",
    lineHeight: "32px",
  },
} as const;

export const layout = {
  contentTop: 104,
  maxWidth: 800,
  cardContentMaxWidth: 450,
  headlineMaxWidth: 340,
  floatingCtaSpace: 88,
} as const;
